import { useEffect, useRef, useState } from 'react';
import './player-quest-page.styles.scss';
import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import PlayerCheckpointPage from './player-checkpoint-page.component';
import Modal, { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import { getQueryFromFirestore } from '@/utils/getDataFromDbSimplified';
import { checkElementAccess } from '@/utils/checkElementAccess';
import BackButton from '@/components/SHARED/back-button/back-button.component';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faCheckCircle, faFlag, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import linkifyHtml from 'linkify-html';

const PlayerQuestPage = ({
  gameData,
  gameElements,
  gameStats,
  teamMembers,
  playerData,
  playerProgress
}) => {

  const { questPath } = useParams();
  const [questData, setQuestData] = useState(null);
  const [access, setAccess] = useState(false);
  const [lockedPreview, setLockedPreview] = useState(false);
  const [lockedReasons, setLockedReasons] = useState([]);
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMapImage, setShowMapImage] = useState(false);
  const screenSize = useSelector(state => state.contentData.screenSize);
  const imgRef = useRef(null);
  const questListenerRef = useRef(null);
  const approveRef = useRef();
  // the access check re-runs on every gameStats/playerData change, so only
  // alert once per quest instead of re-opening the modal each time
  const deniedAlertRef = useRef(null);
  const navigate = useNavigate();

  const gameId = gameData?.gameId;
  const questProgress = (questData && playerProgress?.[questData.id]) || null;

  useEffect(() => {
    if (!gameId || !questPath) return;
    setQuestData(null);
    setAccess(false);
    setLockedPreview(false);
    setLoading(true);
    getQuestData();

    function showNotFound() {
      setQuestData(null);
      setLoading(false);
      setAlertMessage('Quest not found.');
      approveRef.current = () => {
        navigate(-1);
        approveRef.current = null;
      }
    }

    function getQuestData() {
      getQueryFromFirestore({
        'path': `games/${gameId}/gameElements`,
        'queryArray': [{
          'field': 'type',
          'operator': '==',
          'value': 'quest'
        }, {
          'field': 'path',
          'operator': '==',
          'value': questPath
        }],
        'callback': callback,
        'emptyCallback': showNotFound,
        'handleListener': handleListener
      })
      function callback(quest) {
        if (!quest || quest.status !== 'active' || !quest.opt_visible) {
          showNotFound();
          return;
        }
        setQuestData(quest);
        setLoading(false);
      }
      function handleListener(unsub) {
        questListenerRef.current = unsub;
      }
    }
    return () => {
      if (questListenerRef.current) {
        questListenerRef.current();
        questListenerRef.current = null;
      }
    }
  }, [gameId, questPath, navigate])

  useEffect(() => {
    if (!questData || !playerData) return;
    if (!gameElements || !gameStats) return;
    const accessResult = checkElementAccess({
      playerData,
      gameElements: {
        ...gameElements,
        [questData.id]: questData
      },
      elementId: questData.id,
      gameStats,
      teamMembers,
      type: 'quest'
    });
    const hasAccess = !!accessResult?.access;
    // A locked quest with a preview enabled stays viewable, showing the
    // access requirements in place of the checkpoints.
    const showPreview = !!accessResult && !hasAccess && !!questData.opt_preview;
    setAccess(hasAccess);
    setLockedPreview(showPreview);
    setLockedReasons(showPreview ? (accessResult.arr || []) : []);
    if (hasAccess || showPreview) {
      deniedAlertRef.current = null;
      return;
    }
    if (deniedAlertRef.current === questData.id) return;
    deniedAlertRef.current = questData.id;
    setAlertMessage(accessResult
      ? `You do not meet the requirements to access this quest. ` +
        `Missing: ${(accessResult.arr || []).map(r => r.text).join(', ')}`
      : 'Quest not found.');
    approveRef.current = () => {
      navigate(-1);
      approveRef.current = null;
    }
  }, [questData, playerData, gameElements, gameStats, teamMembers, navigate])

  function isCheckpointComplete(checkpoint) {
    return playerProgress?.[questData?.id]?.checkpoints?.[checkpoint.id]?.status === 'approved';
  }

  function isCheckpointPending(checkpoint) {
    return playerProgress?.[questData?.id]?.checkpoints?.[checkpoint.id]?.status === 'pending';
  }

  function isCheckpointAccessible(index) {
    if (index === 0) return true;
    const checkpoints = questData?.checkpoints || [];
    return isCheckpointComplete(checkpoints[index - 1]);
  }

  function handleCheckpointClick(checkpoint, index) {
    if (!isCheckpointAccessible(index)) return;
    navigate(`checkpoint/${checkpoint.path}`);
  }

  const checkpoints = questData?.checkpoints || [];

  return (
    <div className='player-quest-page'>
      {
        loading
          ? <div className='center'><ThreeDotsLoader /></div>
          : (questData && (access || lockedPreview))
            ? <Routes>
              <Route path='' element={
                <div className='player-quest-content g-card'>
                  <BackButton cancel={() => navigate(-1)} />
                  <div className='g-space-1'></div>
                  <div className={`player-quest-header ${screenSize}`}>
                    <div className='quest-icon-div'>
                      <ImageContainer
                        src={questData.icon}
                        alt={`${questData.name} quest icon`}
                        className='quest-icon'
                        preview={true} />
                    </div>
                    <div className='quest-header-text'>
                      <h2>{questData.name}</h2>
                      {
                        questData.desc &&
                        <div
                          className='quest-desc'
                          dangerouslySetInnerHTML={{ __html: linkifyHtml(questData.desc) }}>
                        </div>
                      }
                    </div>
                  </div>
                  <hr />
                  <div className={`quest-layout ${screenSize}`}>

                    {/* LEFT: Map with checkpoint pins */}
                    <div className='quest-map-display'>
                      <div className='quest-map-header'>
                        <h3>Quest Map</h3>
                        <button
                          className='g-button small-btn'
                          onClick={() => setShowMapImage(true)}>
                          <FontAwesomeIcon icon={faMagnifyingGlass} />
                        </button>
                      </div>
                      <div className="g-space-1"></div>
                      {
                        questData.mapImageUrl
                          ? <div className='qmap-image-wrapper' ref={imgRef}>
                            <img
                              src={
                                questData.mapImageUrl.includes('http')
                                  ? questData.mapImageUrl
                                  : `https://app.gamably.com${questData.mapImageUrl}`
                              }
                              alt='Quest Map'
                              className='qmap-image'
                              draggable={false}
                            />
                            {checkpoints.length > 1 && (
                              <svg
                                className='qmap-svg-overlay'
                                viewBox='0 0 100 100'
                                preserveAspectRatio='none'>
                                <defs>
                                  <filter id='qmap-line-shadow' x='-20%' y='-20%' width='140%' height='140%'>
                                    <feDropShadow dx='0' dy='2' stdDeviation='1.5' floodColor='rgba(0,0,0,0.4)' />
                                  </filter>
                                </defs>
                                {checkpoints.slice(1).map((pt, i) => (
                                  <line
                                    key={i}
                                    x1={checkpoints[i].xPct * 100}
                                    y1={checkpoints[i].yPct * 100}
                                    x2={pt.xPct * 100}
                                    y2={pt.yPct * 100}
                                    className='qmap-line'
                                    filter='url(#qmap-line-shadow)'
                                  />
                                ))}
                              </svg>
                            )}
                            {checkpoints.map((pt, i) => {
                              const complete = isCheckpointComplete(pt);
                              const pending = isCheckpointPending(pt);
                              const accessible = isCheckpointAccessible(i);
                              return (
                                <div
                                  key={pt.id}
                                  className={`qmap-pin ${complete
                                    ? 'qmap-pin--complete'
                                    : pending
                                      ? 'qmap-pin--pending'
                                      : accessible
                                        ? 'qmap-pin--available'
                                        : 'qmap-pin--locked'}`}
                                  style={{ left: `${pt.xPct * 100}%`, top: `${pt.yPct * 100}%` }}
                                >
                                  <span className='qmap-pin-label'>{i + 1}</span>
                                </div>
                              );
                            })}
                          </div>
                          : <p className='meta'>No map available for this quest.</p>
                      }
                    </div>

                    {/* RIGHT: Checkpoint buttons (or lock requirements in preview mode) */}
                    <div className='quest-checkpoints'>
                      {
                        lockedPreview
                          ? <div className='quest-locked-reasons'>
                            <h3><FontAwesomeIcon icon={faLock} /> Locked</h3>
                            <p className='quest-locked-intro'>
                              Complete the following to unlock this quest:
                            </p>
                            <ul className='quest-locked-reasons-list'>
                              {
                                lockedReasons.map(reason => (
                                  <li key={reason.id} className={reason.class || ''}>
                                    {reason.text}
                                  </li>
                                ))
                              }
                            </ul>
                          </div>
                          : <>
                            <h3>Checkpoints</h3>
                            <div className='checkpoint-button-list'>
                              {
                                checkpoints.length > 0
                                  ? checkpoints.map((cp, i) => {
                                    const complete = isCheckpointComplete(cp);
                                    const pending = isCheckpointPending(cp);
                                    const accessible = isCheckpointAccessible(i);
                                    return (
                                      <button
                                        key={cp.id}
                                        className={`g-button checkpoint-btn ${complete
                                          ? 'complete'
                                          : pending
                                            ? 'pending'
                                            : accessible
                                              ? 'available'
                                              : 'locked'}`}
                                        disabled={!accessible && !complete}
                                        onClick={() => handleCheckpointClick(cp, i)}
                                      >
                                        <span className='checkpoint-number'>{i + 1}</span>
                                        <span className='checkpoint-name'>{cp.name}</span>
                                        <span className='checkpoint-status-icon'>
                                          {complete
                                            ? <FontAwesomeIcon icon={faCheckCircle} />
                                            : !accessible
                                              ? <FontAwesomeIcon icon={faLock} />
                                              : <FontAwesomeIcon icon={faFlag} />
                                          }
                                        </span>
                                      </button>
                                    );
                                  })
                                  : <p className='meta'>No checkpoints found for this quest.</p>
                              }
                            </div>
                          </>
                      }
                    </div>

                  </div>
                </div>
              } />
              <Route path='checkpoint/:checkpointPath' element={
                // a locked preview only shows the quest overview, never checkpoint content
                access
                  ? <PlayerCheckpointPage
                    questData={questData}
                    gameData={gameData}
                    gameElements={gameElements}
                    gameStats={gameStats}
                    teamMembers={teamMembers}
                    playerData={playerData}
                    questProgress={questProgress}
                  />
                  : <div className='g-card'>
                    <BackButton cancel={() => navigate(-1)} />
                    <p className='meta'>This quest is locked.</p>
                  </div>
              } />
            </Routes>
            : (!loading && !questData)
              ? <div className='g-card'>
                <BackButton cancel={() => navigate(-1)} />
              </div>
              : null
      }
      <ModalAlert
        show={!!alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage}
        onApprove={approveRef.current} />
      <ModalConfirm
        show={!!confirmMessage}
        cancel={() => setConfirmMessage(null)}
        message={confirmMessage}
        onApprove={approveRef.current} />
      {
        (questData && questData.mapImageUrl && showMapImage) &&
        <Modal
          show={showMapImage}
          cancel={() => setShowMapImage(false)}
          text={`${questData.name} Map`}
          closeButton={true}
          cls="wide" >
          <div className='quest-map-modal-content'>
            <ImageContainer
              src={questData.mapImageUrl}
              alt={`${questData.name} map`}
              className='quest-map-modal-image' />
          </div>
        </Modal>
      }
    </div>
  )
}

export default PlayerQuestPage
